import React, { useState, useRef, useCallback } from 'react';
import Papa from 'papaparse';

const ImportCSVModal = ({ isOpen, onClose, onSave }) => {
  const [previewData, setPreviewData] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isImporting, setIsImporting] = useState(false);
  const fileInputRef = useRef(null);

  const handleCancel = useCallback(() => {
    setPreviewData([]); // Limpa a tabela
    setSelectedFile(null); // Limpa o arquivo selecionado
    if (fileInputRef.current) {
        fileInputRef.current.value = ''; // Reseta o seletor de arquivos do sistema
    }
    onClose(); // Fecha o modal
}, [onClose]);

  if (!isOpen) return null;

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setSelectedFile(file);

    Papa.parse(file, {
      delimiter: ";",
      header: false, // Assumindo que a primeira linha é cabeçalho
      skipEmptyLines: true,
      encoding: "ISO-8859-1",
      complete: (results) => {
        const dataRows = results.data.slice(1); // Pula o cabeçalho
        const processed = dataRows.map((cols) => {
            const code = cols[8]?.trim() || crypto.randomUUID(); // Usa o código ou gera um se não tiver
            const expiry = cols[7]?.trim() || 'sem-validade';
            // Criamos um ID único para o LOTE (Código + Validade)
            // Exemplo: 789123456-15-10-2026
            const compositeId = `${code}-${expiry.replace(/\//g, '-')}`;
          if (cols.length < 9) return null;
          return {
            id: compositeId, // Este ID será o nome do documento no Firestore
            name: cols[0]?.trim() || '',
            brand: cols[1]?.trim() || '',
            category: cols[2]?.trim() || '',
            cost: cleanNumber(cols[3]),
            salePrice: cleanNumber(cols[4]),
            initialQty: cleanNumber(cols[5]),
            soldQty: 0, // Inicializa zerado para o estoque funcionar
            purchaseDate: parseDate(cols[6]),
            expiry: parseDate(cols[7]),
            code: code, // Mantemos o código de barras original no campo 'code'
          };
        }).filter(Boolean);
        setPreviewData(processed);
      }
    });
  };

  // Limpeza de números (Trata "1.250,50" -> 1250.50)
  const cleanNumber = (value) => {
    if (!value) return 0;
    const str = value.toString().trim();
    const cleaned = str.replace(/\./g, '').replace(',', '.');
    const num = parseFloat(cleaned);
    return isNaN(num) ? 0 : num;
  };

  // Parsing de data (Trata "DD/MM/YYYY" -> Date Object)
  const parseDate = (dateStr) => {
    if (!dateStr || dateStr.trim() === '') return null;
    const parts = dateStr.trim().split('/');
    if (parts.length !== 3) return null;
    const [day, month, year] = parts;
    return new Date(`${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}T00:00:00`);
  };

  const handleImport = async () => {
    if (previewData.length === 0) return;
    setIsImporting(true);
    try {
      await onSave(previewData);
      setPreviewData([]);
      setSelectedFile(null);
      onClose();
    } catch (error) {
      console.error("Erro na importação:", error);
      alert("Erro ao importar dados. Verifique o console.");
    } finally {
      setIsImporting(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="bg-white rounded-2xl shadow-2xl p-6 sm:p-8 w-full max-w-4xl m-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-800">Importar Itens de Estoque</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl">&times;</button>
        </div>

        <div className="space-y-6">
          {/* Área de Seleção */}
          <div className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${selectedFile ? 'border-green-200 bg-green-50' : 'border-[#D7BFBD] bg-gray-50'}`}>
            <input ref={fileInputRef} type="file" accept=".csv" className="hidden" onChange={handleFileChange} />
            {!selectedFile ? (
              <div>
                <p className="text-gray-600 mb-4">Selecione sua planilha .CSV (Separador: ponto e vírgula)</p>
                <button onClick={() => fileInputRef.current?.click()} className="custom-button w-full sm:w-auto font-semibold py-2 px-4 shadow-sm text-sm">Escolher Arquivo</button>
              </div>
            ) : (
              <p className="text-green-700 font-medium">✅ Arquivo carregado: {selectedFile.name}</p>
            )}
          </div>

          {/* Preview da Tabela */}
          {previewData.length > 0 && (
            <div className="overflow-x-auto border rounded-lg">
              <table className="w-full text-sm text-left">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="p-3">Produto</th>
                    <th className="p-3">Marca</th>
                    <th className="p-3 text-right">Custo</th>
                    <th className="p-3 text-right">Venda</th>
                    <th className="p-3 text-center">Qtd</th>
                    <th className="p-3">Compra</th>
                    <th className="p-3">Validade</th>
                    <th className="p-3">Código</th>
                  </tr>
                </thead>
                <tbody>
                  {previewData.slice(0, 5).map((item, index) => (
                    <tr key={index} className="border-b">
                      <td className="p-3 font-medium">{item.name}</td>
                      <td className="p-3">{item.brand}</td>
                      <td className="p-3 text-right">R$ {item.cost.toFixed(2)}</td>
                      <td className="p-3 text-right">R$ {item.salePrice.toFixed(2)}</td>
                      <td className="p-3 text-center">{item.initialQty}</td>
                      <td className="p-3">{item.purchaseDate ? item.purchaseDate.toLocaleDateString('pt-BR') : '-'}</td>
                      <td className="p-3">{item.expiry ? item.expiry.toLocaleDateString('pt-BR') : '-'}</td>
                      <td className="p-3 font-mono text-xs">{item.code}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {previewData.length > 5 && (
                <p className="p-3 text-center text-gray-500 italic">E mais {previewData.length - 5} itens...</p>
              )}
            </div>
          )}

          {/* Botões de Ação */}
          <div className="flex flex-col-reverse sm:flex-row justify-end gap-4 pt-4 border-t">
            <button onClick={handleCancel} className="py-2 px-6 bg-gray-200 text-gray-800 font-semibold rounded-lg hover:bg-gray-300">Cancelar</button>
            <button 
              onClick={handleImport} 
              disabled={previewData.length === 0 || isImporting}
              className={`custom-button w-full sm:w-auto font-semibold py-2 px-4 rounded-lg shadow-sm text-sm ${isImporting ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {isImporting ? 'Importando...' : `Confirmar Importação (${previewData.length} itens)`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImportCSVModal;

